import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  bankAccountsApi,
  costCentersApi,
  subcategoriesApi,
  transactionsApi,
} from "../../../services";
import type {
  BankAccountAttributes,
  CostCenterAttributes,
  SubcategoryAttributes,
  TransactionAttributes,
} from "../../../types";
import { CreateInputTransactionModal } from "../Transaction/CreateInputTransactionModal";
import { CreateTransactionModal } from "../Transaction/CreateTransactionModal";
import { formatCurrency, formatDate } from "../../../utils/formatUtils";
import {
  getBrazilCalendarMonthRange,
  getTodayInBrazil,
  TIMEZONE,
} from "../../../utils/dateUtils";
import { AdminPageHeader } from "../shared";
import { ErrorState, Icons, IconsLarge, Loading, useToaster } from "../../shared";

/** Em qual data cada lançamento é posicionado na grade (padrão: vencimento). */
type CalendarAnchor = "due" | "payment";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function isInstallmentTransaction(tx: TransactionAttributes): boolean {
  return Boolean(tx.installment?.trim());
}

function isRecurrencyTransaction(tx: TransactionAttributes): boolean {
  return tx.recurrencyId != null && tx.recurrencyId !== 0;
}

function calendarTransactionTooltipLines(
  tx: TransactionAttributes,
  labels: { bank: string; costCenter: string; subcategory: string }
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [
    { label: "ID", value: String(tx.id) },
    { label: "Tipo", value: tx.type === "input" ? "Entrada" : "Saída" },
    { label: "Valor", value: formatCurrency(tx.amount) },
  ];
  if (tx.description?.trim()) {
    rows.push({ label: "Descrição", value: tx.description.trim() });
  }
  if (tx.observation?.trim()) {
    rows.push({ label: "Observação", value: tx.observation.trim() });
  }
  rows.push(
    { label: "Competência", value: formatDate(tx.competenceDate) },
    { label: "Vencimento", value: formatDate(tx.dueDate) },
    { label: "Pagamento", value: formatDate(tx.paymentDate) }
  );
  if (tx.paymentMethod?.trim()) {
    rows.push({ label: "Forma de pagamento", value: tx.paymentMethod.trim() });
  }
  rows.push(
    { label: "Conta", value: labels.bank },
    { label: "Subcategoria", value: labels.subcategory },
    { label: "Centro de custo", value: labels.costCenter }
  );
  if (tx.client?.trim()) {
    rows.push({ label: "Cliente", value: tx.client.trim() });
  }
  if (tx.product?.trim()) {
    rows.push({ label: "Produto", value: tx.product.trim() });
  }
  if (tx.installment?.trim()) {
    rows.push({ label: "Parcelamento", value: tx.installment.trim() });
  }
  if (tx.recurrencyId != null && tx.recurrencyId !== 0) {
    rows.push({ label: "Recorrência", value: `Grupo #${tx.recurrencyId}` });
  }
  return rows;
}

function CalendarTransactionTooltipBody({
  tx,
  labels,
}: {
  tx: TransactionAttributes;
  labels: { bank: string; costCenter: string; subcategory: string };
}) {
  const rows = calendarTransactionTooltipLines(tx, labels);
  return (
    <div className="space-y-1 text-[11px] leading-snug">
      {rows.map((row) => (
        <div key={row.label} className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0 text-left">
          <span className="text-slate-400 whitespace-nowrap">{row.label}</span>
          <span className="text-slate-50 font-medium text-right break-words">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function parseIsoDate(dateStr: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!m) return null;
  return {
    y: Number(m[1]),
    m: Number(m[2]),
    d: Number(m[3]),
  };
}

/** YYYY-MM-DD para agrupar no calendário (aceita ISO completo; alinha ao fuso BR). */
function toCalendarDayKey(dateStr: string | null | undefined): string | null {
  if (!dateStr?.trim()) return null;
  const raw = dateStr.trim();
  const ymd = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-CA", { timeZone: TIMEZONE });
  } catch {
    return null;
  }
}

/** Retorna a chave do dia se a data cai no mês exibido; caso contrário null. */
function dayKeyInMonth(
  dateStr: string | null,
  year: number,
  month: number
): string | null {
  const key = toCalendarDayKey(dateStr);
  if (!key) return null;
  const p = parseIsoDate(key);
  if (!p || p.y !== year || p.m !== month) return null;
  return key;
}

/** Primeiro dia da grade (segunda-feira como coluna 0). */
function buildCalendarCells(year: number, month: number): (number | null)[] {
  const lastDay = new Date(year, month, 0).getDate();
  const jsWeekStart = new Date(year, month - 1, 1).getDay();
  const mondayOffset = (jsWeekStart + 6) % 7;
  const cells: (number | null)[] = [];
  for (let i = 0; i < mondayOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function monthTitle(year: number, month: number): string {
  const d = new Date(year, month - 1, 1);
  const monthLong = d.toLocaleDateString("pt-BR", { month: "long" });
  const capitalized = monthLong.charAt(0).toUpperCase() + monthLong.slice(1);
  return `${capitalized} ${year}`;
}

/** Intervalo YYYY-MM-DD do mês (para filtros da API de lançamentos). */
function monthDateBounds(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return { from: ymd(year, month, 1), to: ymd(year, month, lastDay) };
}

/**
 * GET /transactions filtrando só pela dimensão usada na grade (vencimento ou pagamento).
 */
async function fetchTransactionsForCalendarMonth(
  companyId: number,
  year: number,
  month: number,
  anchor: CalendarAnchor
): Promise<{ transactions: TransactionAttributes[]; error: string | null }> {
  const { from, to } = monthDateBounds(year, month);
  if (anchor === "due") {
    const dueRes = await transactionsApi.getAll(companyId, {
      dueDateFrom: from,
      dueDateTo: to,
    });
    if (!dueRes.success) {
      return {
        transactions: [],
        error: dueRes.error ?? "Falha ao carregar por data de vencimento.",
      };
    }
    return { transactions: dueRes.data ?? [], error: null };
  }

  const payRes = await transactionsApi.getAll(companyId, {
    paymentDateFrom: from,
    paymentDateTo: to,
  });
  if (!payRes.success) {
    return {
      transactions: [],
      error: payRes.error ?? "Falha ao carregar por data de pagamento.",
    };
  }
  return { transactions: payRes.data ?? [], error: null };
}

export const PaymentCalendar: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;

  const initialRange = useMemo(() => getBrazilCalendarMonthRange(), []);
  const initialFrom = initialRange.from;
  const [year, setYear] = useState(() => Number(initialFrom.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(initialFrom.slice(5, 7)));

  const [transactions, setTransactions] = useState<TransactionAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showInput, setShowInput] = useState(true);
  const [showOutput, setShowOutput] = useState(true);
  const [calendarAnchor, setCalendarAnchor] = useState<CalendarAnchor>("due");

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<TransactionAttributes | null>(null);
  const [createFormVariant, setCreateFormVariant] = useState<"input" | "output">("output");
  const [bankAccounts, setBankAccounts] = useState<BankAccountAttributes[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterAttributes[]>([]);
  const [lookupSubcategories, setLookupSubcategories] = useState<SubcategoryAttributes[]>([]);
  const [modalSubcategories, setModalSubcategories] = useState<SubcategoryAttributes[]>(
    []
  );
  const [modalSubcategoriesLoading, setModalSubcategoriesLoading] = useState(false);
  const [editPrefetchLoading, setEditPrefetchLoading] = useState(false);
  const { showToast } = useToaster();

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const { transactions: rows, error: loadErr } = await fetchTransactionsForCalendarMonth(
      companyId,
      year,
      month,
      calendarAnchor
    );
    setLoading(false);
    if (loadErr && rows.length === 0) {
      setError(loadErr);
      setTransactions([]);
      return;
    }
    setTransactions(rows);
    setError(loadErr);
  }, [companyId, year, month, calendarAnchor]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!companyId) {
      setBankAccounts([]);
      setCostCenters([]);
      setLookupSubcategories([]);
      return;
    }
    let cancelled = false;
    void Promise.all([
      bankAccountsApi.getAll(companyId),
      costCentersApi.getAll(companyId),
      subcategoriesApi.getAll(companyId, undefined, "input"),
      subcategoriesApi.getAll(companyId, undefined, "output"),
    ]).then(([accRes, costRes, subIn, subOut]) => {
      if (cancelled) return;
      if (accRes.success && accRes.data) setBankAccounts(accRes.data);
      if (costRes.success && costRes.data) setCostCenters(costRes.data);
      const subByKey = new Map<number, SubcategoryAttributes>();
      if (subIn.success) {
        for (const s of subIn.data ?? []) subByKey.set(s.id, s);
      }
      if (subOut.success) {
        for (const s of subOut.data ?? []) subByKey.set(s.id, s);
      }
      setLookupSubcategories(Array.from(subByKey.values()));
    });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!showCreateForm || companyId === 0) {
      setModalSubcategories([]);
      return;
    }
    let cancelled = false;
    setModalSubcategoriesLoading(true);
    void subcategoriesApi.getAll(companyId, undefined, createFormVariant).then((res) => {
      if (!cancelled && res.success && res.data) setModalSubcategories(res.data);
      if (!cancelled) setModalSubcategoriesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [showCreateForm, companyId, createFormVariant]);

  const handleEditTransaction = async (transactionId: number) => {
    setEditPrefetchLoading(true);
    try {
      const res = await transactionsApi.getById(transactionId);
      if (!res.success || !res.data) {
        showToast("error", res.error ?? "Não foi possível carregar o lançamento para edição.");
        return;
      }
      if (res.data.companyId !== companyId) {
        showToast("error", "Este lançamento não pertence à empresa selecionada.");
        return;
      }
      setEditingTransaction(res.data);
      setCreateFormVariant(res.data.type === "input" ? "input" : "output");
      setShowCreateForm(true);
    } catch {
      showToast("error", "Não foi possível carregar o lançamento para edição.");
    } finally {
      setEditPrefetchLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowCreateForm(false);
    setEditingTransaction(null);
  };

  const handleModalSuccess = () => {
    void load();
    setShowCreateForm(false);
    setEditingTransaction(null);
  };

  const todayStr = useMemo(() => getTodayInBrazil(), []);

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, TransactionAttributes[]>();
    const push = (dateKey: string, tx: TransactionAttributes) => {
      const list = map.get(dateKey) ?? [];
      list.push(tx);
      map.set(dateKey, list);
    };

    for (const t of transactions) {
      if (!showInput && t.type === "input") continue;
      if (!showOutput && t.type === "output") continue;

      if (calendarAnchor === "due") {
        const dk = dayKeyInMonth(t.dueDate, year, month);
        if (dk) push(dk, t);
      } else {
        const pk = dayKeyInMonth(t.paymentDate, year, month);
        if (pk) push(pk, t);
      }
    }

    for (const [, list] of map) {
      list.sort((a, b) => {
        const ta = a.type;
        const tb = b.type;
        if (ta !== tb) return ta === "input" ? -1 : 1;
        return (b.amount ?? 0) - (a.amount ?? 0);
      });
    }

    return map;
  }, [transactions, year, month, showInput, showOutput, calendarAnchor]);

  const cells = useMemo(() => buildCalendarCells(year, month), [year, month]);

  const goPrevMonth = () => {
    if (month <= 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (month >= 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const typesDisabled = !showInput && !showOutput;

  const bankById = useMemo(
    () => new Map(bankAccounts.map((b) => [b.id, b.name] as const)),
    [bankAccounts]
  );
  const costById = useMemo(
    () => new Map(costCenters.map((c) => [c.id, c.name] as const)),
    [costCenters]
  );
  const subById = useMemo(
    () => new Map(lookupSubcategories.map((s) => [s.id, s.name] as const)),
    [lookupSubcategories]
  );

  if (!company) {
    return (
      <div className="max-w-6xl mx-auto">
        <ErrorState title="Empresa" message="Selecione uma empresa para ver o calendário." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
      <AdminPageHeader
        icon={<IconsLarge.Calendar />}
        title="Calendário de pagamentos"
        subtitle="Lançamentos por dia — escolha posicionar pela data de vencimento ou de pagamento."
        ctaStrip={
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <button
                type="button"
                onClick={goPrevMonth}
                className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-[#374151] text-gray-800 dark:text-slate-100 hover:bg-gray-200 dark:hover:bg-[#4B5563] font-medium text-sm"
                aria-label="Mês anterior"
              >
                ‹
              </button>
              <span className="min-w-[180px] text-center text-base font-semibold text-gray-900 dark:text-white">
                {monthTitle(year, month)}
              </span>
              <button
                type="button"
                onClick={goNextMonth}
                className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-[#374151] text-gray-800 dark:text-slate-100 hover:bg-gray-200 dark:hover:bg-[#4B5563] font-medium text-sm"
                aria-label="Próximo mês"
              >
                ›
              </button>
            </div>

            <div className="flex flex-wrap gap-4 sm:gap-6 items-center justify-center lg:justify-end">
              <fieldset className="flex flex-wrap gap-3 border-0 p-0 m-0">
                <legend className="sr-only">Tipo de movimento</legend>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInput}
                    onChange={(e) => setShowInput(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Entradas
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOutput}
                    onChange={(e) => setShowOutput(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Saídas
                </label>
              </fieldset>
              <fieldset className="flex flex-wrap items-center gap-2 sm:gap-3 border-0 p-0 m-0">
                <legend className="text-xs font-medium text-gray-500 dark:text-slate-400 whitespace-nowrap">
                  Exibir por
                </legend>
                <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="radio"
                    name="calendar-anchor"
                    checked={calendarAnchor === "due"}
                    onChange={() => setCalendarAnchor("due")}
                    className="border-gray-300 dark:border-gray-600 text-sky-600 focus:ring-sky-500"
                  />
                  Data de vencimento
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="radio"
                    name="calendar-anchor"
                    checked={calendarAnchor === "payment"}
                    onChange={() => setCalendarAnchor("payment")}
                    className="border-gray-300 dark:border-gray-600 text-sky-600 focus:ring-sky-500"
                  />
                  Data de pagamento
                </label>
              </fieldset>
            </div>
          </div>
        }
      />

      {typesDisabled && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          Ative pelo menos um tipo: Entradas ou Saídas.
        </div>
      )}

      <CreateInputTransactionModal
        isOpen={showCreateForm && createFormVariant === "input"}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        companyId={companyId}
        subcategories={createFormVariant === "input" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "input" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "input" ? editingTransaction : null}
      />

      <CreateTransactionModal
        isOpen={showCreateForm && createFormVariant === "output"}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        companyId={companyId}
        subcategories={createFormVariant === "output" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "output" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "output" ? editingTransaction : null}
      />

      {loading ? (
        <Loading message="Carregando lançamentos..." size="lg" />
      ) : error && transactions.length === 0 ? (
        <ErrorState title="Calendário" message={error} />
      ) : (
        <div className="bg-white dark:bg-[#2D3748] rounded-xl border border-gray-100 dark:border-[#374151] shadow-medium overflow-hidden">
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-[#374151] border-b border-gray-200 dark:border-[#374151]">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="bg-gray-50 dark:bg-[#1e293b] text-center text-xs font-semibold text-gray-600 dark:text-slate-400 py-2"
              >
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-[#374151]">
            {cells.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${idx}`}
                    className="min-h-[100px] sm:min-h-[120px] bg-slate-50/80 dark:bg-[#1A202C]/60"
                  />
                );
              }
              const dateKey = ymd(year, month, day);
              const isToday = dateKey === todayStr;
              const list = occurrencesByDate.get(dateKey) ?? [];

              return (
                <div
                  key={dateKey}
                  className={`min-h-[100px] sm:min-h-[120px] bg-white dark:bg-[#2D3748] p-1.5 flex flex-col ${
                    isToday ? "ring-2 ring-sky-500/70 ring-inset" : ""
                  }`}
                >
                  <div className="flex justify-between items-start gap-1 mb-1">
                    <span
                      className={`text-xs font-semibold tabular-nums ${
                        isToday
                          ? "text-sky-600 dark:text-sky-300"
                          : "text-gray-700 dark:text-slate-200"
                      }`}
                    >
                      {day}
                    </span>
                  </div>
                  <div className="flex-1 space-y-1 overflow-y-auto max-h-[140px] sm:max-h-[180px] pr-0.5">
                    {list.map((tx, i) => {
                      const isIn = tx.type === "input";
                      const amountStr =
                        isIn && tx.amount > 0
                          ? `+ ${formatCurrency(tx.amount)}`
                          : isIn
                            ? formatCurrency(tx.amount)
                            : `- ${formatCurrency(Math.abs(tx.amount))}`;
                      const tooltipLabels = {
                        bank: bankById.get(tx.bankAccountId) ?? `Conta #${tx.bankAccountId}`,
                        costCenter:
                          tx.costCenterId != null
                            ? costById.get(tx.costCenterId) ?? `CC #${tx.costCenterId}`
                            : "—",
                        subcategory:
                          subById.get(tx.subcategoryId) ?? `Subcategoria #${tx.subcategoryId}`,
                      };
                      const showInstallmentIcon = isInstallmentTransaction(tx);
                      const showRecurrencyIcon = isRecurrencyTransaction(tx);
                      return (
                        <Tooltip
                          key={`${tx.id}-${i}`}
                          title={
                            <CalendarTransactionTooltipBody
                              tx={tx}
                              labels={tooltipLabels}
                            />
                          }
                          enterDelay={350}
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#0f172a",
                                color: "#f8fafc",
                                border: "1px solid #334155",
                                maxWidth: 320,
                                px: 1.5,
                                py: 1.25,
                                boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
                              },
                            },
                          }}
                        >
                          <span className="block w-full">
                            <button
                              type="button"
                              disabled={editPrefetchLoading}
                              onClick={() => void handleEditTransaction(tx.id)}
                              className={`w-full rounded px-1 py-0.5 text-left text-[10px] sm:text-xs leading-tight border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#2D3748] enabled:hover:brightness-95 dark:enabled:hover:brightness-110 disabled:opacity-50 disabled:cursor-wait ${
                                isIn
                                  ? "bg-emerald-500/10 border-emerald-600/30 text-emerald-800 dark:text-emerald-200"
                                  : "bg-red-500/10 border-red-600/30 text-red-800 dark:text-red-200"
                              }`}
                            >
                              <div className="flex items-center justify-end gap-0.5 font-medium min-w-0">
                                {showInstallmentIcon || showRecurrencyIcon ? (
                                  <span className="inline-flex items-center gap-px flex-shrink-0 opacity-90 mr-0.5">
                                    {showInstallmentIcon ? (
                                      <span title="Parcelamento" className="inline-flex">
                                        <Icons.Installment
                                          sx={{ fontSize: 12 }}
                                          className="text-current"
                                          aria-hidden
                                        />
                                      </span>
                                    ) : null}
                                    {showRecurrencyIcon ? (
                                      <span title="Recorrência" className="inline-flex">
                                        <Icons.Recurrency
                                          sx={{ fontSize: 12 }}
                                          className="text-current"
                                          aria-hidden
                                        />
                                      </span>
                                    ) : null}
                                  </span>
                                ) : null}
                                <span className="tabular-nums flex-shrink-0">{amountStr}</span>
                              </div>
                              {tx.description ? (
                                <div className="truncate opacity-90">{tx.description}</div>
                              ) : null}
                            </button>
                          </span>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && transactions.length > 0 ? (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          {error}
        </div>
      ) : null}
    </div>
  );
};
